import _ from "lodash";
/**--external */
import React, { useEffect, useRef } from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";

/**--internal-- */
import withLoader from "UIComponents/GqlWithLoader";
import { getApplicableFiltersQuery } from "../../modules/Queries";
import { getApplicableFiltersFromCache } from "../../modules/GraphqlHelpers";
import {
  getUpdatedModuleFacetsMemoized,
  getFilterLabel,
} from "../../modules/Utils";
import { getApplicableFiltersInfoMemoized } from "../DynamicFiltersWrapper/DynamicFilters/utils";
import SearchInput from "../DynamicFiltersWrapper/DynamicFilters/SearchInput";
import FilterGroup from "../DynamicFiltersWrapper/DynamicFilters/FilterGroup";
import { useI18n } from "Hooks";
import { Button } from "@toddle-edu/ds-web";

/**--relative-- */
import classes from "./DynamicFiltersWrapper.scss";
import DynamicFilter from "./DynamicFilters";
import {
  containerStyle,
  filterGrpContainerStyle,
  resetButtonStyles,
} from "./DynamicFiltersWrapperStyles";
import { getParentFiltersObj } from "./utils";
import MoreFilters from "./MoreFilters";

const DynamicFiltersWrapper = props => {
  const {
    isSearchApplicable,
    areFiltersApplicable,
    searchText,
    applicableFilters,
    placeholder,
    updateSearchText,
    appliedFilters,
    curriculumProgramId,
    updateAppliedFilters,
    activeTab,
    updateFilterOptions,
    updateParentFilter,
    markDependentFilterAsUpdated,
    parentFilterObj,
    dependentFiltersConfig,
    parentFilters,
    updateLoadingFilters,
    plannerElementSets,
    curriculumType,
    isProjectNode,
    projectNodeDetails,
    placeholderLabelParam,
    currentTabFiltersConfig,
    activeTabClassesFilterOptions,
    topRowFilterCount,
    testId,
  } = props;

  const { t } = useI18n([
    "common",
    "snp",
    "progressReport",
    "academicSetup",
    "course",
  ]);

  const containerParentRef = useRef();

  const headerClasses = classNames({
    [classes.header]: areFiltersApplicable,
  });

  const entityTypeInfo = _.get(
    currentTabFiltersConfig,
    `${activeTab}.entityTypeInfo`,
    {}
  );

  const topRowFilters = _.slice(applicableFilters, 0, topRowFilterCount);
  const moreFilters = _.slice(applicableFilters, topRowFilterCount);

  const parentFiltersData = getParentFiltersObj({
    parentFilters,
    entityTypeInfo,
    plannerElementSets,
    t,
    curriculumType,
    appliedFilters,
  });

  const updateInputField = ({ values, key, isParent = false }) => {
    if (isParent) {
      updateParentFilter({ parent: key });
    }

    updateAppliedFilters({
      appliedFilters: { [key]: values },
    });
  };

  useEffect(() => {
    _.forEach(moreFilters, config => {
      const dependentFilterParents = _.get(
        _.find(
          dependentFiltersConfig,
          ({ filter: dependentFilter }) => dependentFilter === config.filter
        ),
        "parents",
        []
      );
      const isDependent = !_.isEmpty(dependentFilterParents);
      if (_.isEmpty(parentFilterObj) || !isDependent) {
        return;
      }

      const dependentFilters = _.get(parentFilterObj, "dependentFilters", []);

      if (!_.includes(dependentFilters, config.filter)) {
        return;
      }

      updateInputField({ values: [], key: config.filter });

      markDependentFilterAsUpdated({ filter: config.filter });
    });
  }, [parentFilterObj]);

  const renderFilter = ({ Component, config, extraProps = {} }) => {
    const filter = config.filter;
    const filterTestId = testId ? `${testId}-filter-${filter}` : undefined;

    const filterData = _.get(entityTypeInfo, filter, {});

    const dependentFilterParents = _.get(
      _.find(
        dependentFiltersConfig,
        ({ filter: dependentFilter }) => dependentFilter === filter
      ),
      "parents",
      []
    );

    const isParent = _.includes(parentFilters, filter);

    const {
      filterLocale,
      isLabelPresentInSetLabels,
      labelPresentInSetLabelsPerm,
    } = filterData;

    const label = getFilterLabel({
      plannerElementSets,
      t,
      filterLocale,
      labelPresentInSetLabelsPerm,
      isLabelPresentInSetLabels,
      entityType: filter,
      curriculumType,
    });

    return (
      <div className={classes.filter}>
        <Component
          entityType={filter}
          parentFiltersData={parentFiltersData}
          values={appliedFilters[filter]}
          dependentFilterParents={dependentFilterParents}
          markDependentFilterAsUpdated={markDependentFilterAsUpdated}
          parentFilterObj={parentFilterObj}
          {...config}
          label={label}
          isParent={isParent}
          curriculumProgramId={curriculumProgramId}
          updateInputField={updateInputField}
          updateFilterOptions={updateFilterOptions}
          updateLoadingFilters={updateLoadingFilters}
          baseSetType={filterData.baseSetType}
          curriculumType={curriculumType}
          isProjectNode={isProjectNode}
          projectNodeDetails={projectNodeDetails}
          activeTab={activeTab}
          t={t}
          testId={filterTestId}
          {...extraProps}
        />
      </div>
    );
  };

  const resetFilters = () => {
    const activeTabFilterKeys = _.keys(appliedFilters);
    let updatedFilters = {};
    _.forEach(activeTabFilterKeys, key => {
      updatedFilters = {
        ...updatedFilters,
        [key]:
          key == "ACADEMIC_YEAR" ? _.get(appliedFilters, "ACADEMIC_YEAR") : [],
      };
    });
    updateAppliedFilters({ appliedFilters: updatedFilters });
  };

  const getActiveMoreFiltersCount = () => {
    return _.sumBy(moreFilters, ({ filter }) => {
      return +(_.size(appliedFilters[filter]) > 0);
    });
  };

  return (
    <div className={headerClasses} ref={containerParentRef}>
      <div className={classes.leftContainer}>
        {isSearchApplicable ? (
          <SearchInput
            searchText={searchText}
            placeholder={t(placeholder, {
              label: placeholderLabelParam ? t(placeholderLabelParam) : "",
            })}
            updateSearchText={updateSearchText}
            key={activeTab}
          />
        ) : null}
      </div>
      {areFiltersApplicable && (
        <React.Fragment>
          <FilterGroup
            filters={topRowFilters}
            showRemoveButton={false}
            containerStyle={filterGrpContainerStyle}
          >
            {({ config }) => renderFilter({ Component: DynamicFilter, config })}
          </FilterGroup>

          {_.size(applicableFilters) > topRowFilterCount && (
            <MoreFilters
              t={t}
              filters={moreFilters}
              activeMoreFiltersCount={getActiveMoreFiltersCount()}
              filterContainerProps={{
                activeTab,
                curriculumType,
                plannerElementSets,
                parentFiltersData,
                appliedFilters,
                curriculumProgramId,
                updateInputField,
                updateFilterOptions,
                isProjectNode,
                projectNodeDetails,
              }}
              currentTabFiltersConfig={currentTabFiltersConfig}
              activeTabClassesFilterOptions={activeTabClassesFilterOptions}
              testId={testId}
            />
          )}
          <div className={classes.resetButtonContainer}>
            <Button
              dsVersion="2.0"
              variant="progressive"
              type="inline"
              size="medium"
              onClick={resetFilters}
              style={resetButtonStyles}
            >
              {t("common:reset")}
            </Button>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    activeTab,
    curriculumType,
    isProjectNode,
    projectNodeDetails,
    currentTabFiltersConfig,
  } = ownProps;

  let applicableFilters = [];

  // Parakh: can have different prop name
  if (isProjectNode) {
    // For projects, we will be using hardcoded data (similar to all, just not coming from BE)
    const applicableFiltersData = _.get(
      projectNodeDetails,
      "applicableFiltersData",
      {}
    );
    const moduleFacets = getUpdatedModuleFacetsMemoized({
      data: applicableFiltersData,
      activeTab,
      curriculumType,
      isProjectNode,
      projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      currentTabFiltersConfig,
    });
    applicableFilters = getApplicableFiltersInfoMemoized({
      moduleFacets,
      activeTab,
      currentTabFiltersConfig,
    });
  }

  return {
    isData: true,
    isLoading: false,
    showLoadingIcon: false,
    containerStyle,
    module: _.get(currentTabFiltersConfig, `${activeTab}.enum`, ""),
    placeholder: _.get(currentTabFiltersConfig, `${activeTab}.placeholder`, ""),
    topRowFilterCount: _.get(
      currentTabFiltersConfig,
      `${activeTab}.topRowFilterCount`,
      2
    ),
    placeholderLabelParam: _.get(
      currentTabFiltersConfig,
      `${activeTab}.placeholderLabelParam`
    ),
    parentFilters: _.get(
      currentTabFiltersConfig,
      `${activeTab}.curriculumWiseParentFilters.${curriculumType}`,
      []
    ),
    dependentFiltersConfig: _.get(
      currentTabFiltersConfig,
      `${activeTab}.dependentFilterConfig`,
      {}
    ),
    isProjectNode,
    applicableFilters,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getApplicableFiltersQuery, {
    name: "getApplicableFiltersQuery",
    alias: "getApplicableFiltersQuery",
    skip: ({ areFiltersApplicable, isProjectNode }) =>
      !areFiltersApplicable || isProjectNode,
    options: ({ module }) => {
      return { fetchPolicy: "cache-and-network", variables: { module } };
    },
    props: ({
      getApplicableFiltersQuery: { networkStatus },
      ownProps: {
        module,
        isData,
        isLoading,
        activeTab,
        curriculumType,
        currentTabFiltersConfig,
      },
    }) => {
      const data = getApplicableFiltersFromCache({ module });
      const moduleFacets = getUpdatedModuleFacetsMemoized({
        data,
        activeTab,
        curriculumType,
        currentTabFiltersConfig,
      });

      return {
        data,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        isData: !_.isEmpty(data) && isData,

        applicableFilters: getApplicableFiltersInfoMemoized({
          moduleFacets,
          activeTab,
          currentTabFiltersConfig,
        }),
      };
    },
  }),
  withLoader
)(DynamicFiltersWrapper);
